import get from 'lodash/get';

import Grid from '~/common/mui/Grids/Grid';
import GridItem from '~/common/mui/Grids/GridItem';

import FormBuilderErrorMessage from './FormBuilderErrorMessage';
import InputMapper from './InputMapper';

export type FormBuilderField = {
  name?: string;
  type?: string;
  label?: string;
  span?: number;
  readOnly?: boolean;
  options?:
    | ReadonlyArray<{
        value?: string | boolean | number;
        label?: string;
      }>
    | React.ReactNode[];
  colStart?: number;
  isHidden?: () => boolean;
  value?: any;
  dataTestId?: string;
  disabled?: boolean;
  containerSx?: Record<string, unknown>;
};

type FormBuilderProps = {
  columns?: number;
  rowGap?: string;
  columnGap?: string;
  dataTestId?: string;
  fields?: FormBuilderField[];
  errors?: any;
  getValues?: (...args: any[]) => any;
  handleChange?: (...args: any[]) => any;
  showErrors?: boolean;
  readOnlyMode?: boolean;
  control?: any;
};

const typeHandlesErrorMessage = new Set(['radioGroup']);

export default function FormBuilder({
  fields,
  columns = 4,
  errors = {},
  getValues,
  readOnlyMode = false,
  showErrors,
  rowGap,
  columnGap,
  control,
}: Readonly<FormBuilderProps>) {
  return (
    <Grid
      sx={{
        marginBottom: '1.5rem',
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: columnGap || '3rem',
        rowGap: rowGap || '1rem',
      }}
    >
      {fields.map((field) => {
        const { name, type, colStart, span, containerSx } = field;
        const isVisible = field.isHidden ? !field.isHidden() : true;
        const isInvalid = showErrors && !!errors[name];
        const isErrorMessageHandled = typeHandlesErrorMessage.has(type);
        const error = get(errors, name);
        return (
          isVisible && (
            <GridItem
              key={name}
              sx={{
                width: '100%',
                ...(field.type === 'checkbox' && {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }),
                ...(() => {
                  if (colStart && span) {
                    return { gridColumn: `${colStart} / span ${span}` };
                  }
                  if (colStart) {
                    return { gridColumn: `${colStart}` };
                  }
                  if (span) {
                    return { gridColumn: `span ${span}` };
                  }
                  return {};
                })(),
                ...(readOnlyMode && {
                  opacity: 0.6,
                  cursor: 'not-allowed',
                  '& *': { pointerEvents: 'none' },
                }),
                ...(!readOnlyMode &&
                  (field.disabled || field.readOnly) && {
                    cursor: 'not-allowed',
                    '& *': { pointerEvents: 'none' },
                  }),
                ...containerSx,
              }}
            >
              <InputMapper
                {...field}
                type={type}
                tabIndex={readOnlyMode ? -1 : 0}
                disabled={field.disabled || readOnlyMode}
                getValues={getValues}
                isInvalid={isInvalid}
                dataTestId={field.dataTestId}
                value={field.value}
                control={control}
                error={error}
              />
              {error && showErrors && !isErrorMessageHandled && (
                <FormBuilderErrorMessage error={error} />
              )}
            </GridItem>
          )
        );
      })}
    </Grid>
  );
}
